// public/js/modules/users.js
import { getCurrentUser } from './rbac.js';

export async function loadUsers() {
  const token = JSON.parse(localStorage.getItem('superb_tracking_auth') || '{}').access_token;
  const res = await window.authenticatedFetch('api/v1/users', {  });
  if (!res.ok) throw new Error('Failed to load users');
  return (await res.json()).users || [];
}

export async function updateUserGroups(username, groups, { suppressNotify = false } = {}) {
  const res = await window.authenticatedFetch(`api/v1/users/${encodeURIComponent(username)}/groups`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ groups, suppressNotify })
  });
  if (!res.ok) {
    let msg = 'Failed to update roles';
    try { msg = (await res.json()).error || msg; } catch {}
    throw new Error(msg);
  }
  return res.json();
}

// Send ONE consolidated "access changed" email after a batch of role/tenant
// changes. `change` = { rolesAdded, rolesRemoved, tenantsAdded, tenantsRemoved }
// (tenants as display names). Best-effort — failure here must not fail the save.
export async function notifyAccessChange(username, change) {
  try {
    await window.authenticatedFetch(`api/v1/users/${encodeURIComponent(username)}/notify-access`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ change }),
    });
  } catch { /* notification is best-effort */ }
}

// Throw the backend's error message (not a generic one) so toasts are useful.
async function okOrThrow(res, fallback) {
  if (res.ok) return res.json();
  let msg = fallback;
  try { msg = (await res.json()).error || msg; } catch {}
  throw new Error(msg);
}

// Create a user by email; Cognito sends an invitation with a temporary
// password. role and tenants are optional and pre-assigned before first login.
export async function createUser({ email, role, tenants }) {
  const res = await window.authenticatedFetch('api/v1/users', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, role: role || null, tenants: tenants || [] }),
  });
  return okOrThrow(res, 'Failed to create user');
}

export async function deleteUser(username) {
  const res = await window.authenticatedFetch(`api/v1/users/${encodeURIComponent(username)}`, { method: 'DELETE' });
  return okOrThrow(res, 'Failed to delete user');
}

// Change the logged-in user's own password (authorized by their access token).
export async function changeOwnPassword(currentPassword, newPassword) {
  const res = await window.authenticatedFetch('api/v1/account/password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  return okOrThrow(res, 'Failed to change password');
}

// action: 'confirm' | 'resend-invite' | 'enable' | 'disable' | 'reset-password'
//       | 'set-password' (extra: { password, permanent })
export async function userAdminAction(username, action, extra = {}) {
  const res = await window.authenticatedFetch(`api/v1/users/${encodeURIComponent(username)}/action`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, ...extra }),
  });
  return okOrThrow(res, `Action failed: ${action}`);
}